import { Box, Flex, Grid, GridItem, Skeleton, Text } from '@chakra-ui/react';
import React, { useCallback } from 'react';

import { HighlightComputedStatus } from './utils';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { dollarNumberWithCommas } from 'packages/common-frontend/utils';

// Type for server-side aggregates
export type InvoiceAggregatesData = {
  dueSoon: { status: string; totalAmount: number; count: number };
  overdue: { status: string; totalAmount: number; count: number };
  paid: { status: string; totalAmount: number; count: number };
  totalCount: number;
};

type InvoicesOverviewCardProps = {
  label: string;
  totalAmount: number;
  totalCount: number;
  icon: React.ReactElement;
  isSelected: boolean;
  onClick: () => void;
};
const InvoicesOverviewCard = ({
  label,
  totalAmount,
  totalCount,
  icon,
  isSelected,
  onClick,
}: InvoicesOverviewCardProps) => {
  return (
    <Flex
      alignItems="center"
      p="24px"
      borderRadius="8px"
      borderColor={isSelected ? colors.black[95] : colors.gray[100]}
      borderWidth="1px"
      onClick={onClick}
    >
      <Box mr="24px">{icon}</Box>
      <Box>
        <Text color={colors.black[70]} mb="4px">
          {label}
        </Text>

        <Text fontWeight={600} fontSize="24px" mb="4px">
          {dollarNumberWithCommas(totalAmount)}
        </Text>

        <Text color={colors.black[70]}>{`${totalCount} invoice${
          totalCount === 1 ? '' : 's'
        }`}</Text>
      </Box>
    </Flex>
  );
};

type InvoicesOverviewCardSkeletonProps = {
  label: string;
  icon: React.ReactElement;
};
const InvoicesOverviewCardSkeleton = ({
  label,
  icon,
}: InvoicesOverviewCardSkeletonProps) => {
  return (
    <Flex
      alignItems="center"
      p="24px"
      borderRadius="8px"
      borderColor={colors.gray[100]}
      borderWidth="1px"
    >
      <Box mr="24px">{icon}</Box>
      <Box>
        <Text color={colors.black[70]} mb="4px">
          {label}
        </Text>

        <Skeleton h="32px" w="200px" mb="4px" />
        <Skeleton h="20px" w="80px" />
      </Box>
    </Flex>
  );
};

const InvoicesOverviewCardsSkeleton = () => {
  return (
    <Grid templateColumns="repeat(3, 1fr)" gap="24px" cursor="default">
      <GridItem>
        <InvoicesOverviewCardSkeleton
          label="Due Soon"
          icon={<Icon icon="calendar-clock" color={colors.orange[400]} />}
        />
      </GridItem>
      <GridItem>
        <InvoicesOverviewCardSkeleton
          label="Overdue"
          icon={<Icon icon="calendar-clock" color={colors.red[400]} />}
        />
      </GridItem>
      <GridItem>
        <InvoicesOverviewCardSkeleton
          label="Paid"
          icon={<Icon icon="circle-check" color={colors.green[400]} />}
        />
      </GridItem>
    </Grid>
  );
};

type InvoicesOverviewCardsWithDataProps = {
  aggregates: InvoiceAggregatesData;
  selectedComputedStatus: HighlightComputedStatus | null;
  setSelectedComputedStatus: React.Dispatch<
    React.SetStateAction<HighlightComputedStatus | null>
  >;
};
const InvoicesOverviewCardsWithData = ({
  aggregates,
  selectedComputedStatus,
  setSelectedComputedStatus,
}: InvoicesOverviewCardsWithDataProps) => {
  const onClickOverviewStatusCard = useCallback(
    (computedStatus: HighlightComputedStatus) => {
      if (computedStatus === selectedComputedStatus) {
        setSelectedComputedStatus(null);
      } else {
        setSelectedComputedStatus(computedStatus);
      }
    },
    [selectedComputedStatus, setSelectedComputedStatus],
  );

  return (
    <Grid templateColumns="repeat(3, 1fr)" gap="24px" cursor="pointer">
      <GridItem>
        <InvoicesOverviewCard
          label="Due Soon"
          totalAmount={aggregates.dueSoon.totalAmount}
          totalCount={aggregates.dueSoon.count}
          icon={<Icon icon="calendar-clock" color={colors.orange[400]} />}
          isSelected={selectedComputedStatus === 'dueSoon'}
          onClick={() => onClickOverviewStatusCard('dueSoon')}
        />
      </GridItem>
      <GridItem>
        <InvoicesOverviewCard
          label="Overdue"
          totalAmount={aggregates.overdue.totalAmount}
          totalCount={aggregates.overdue.count}
          icon={<Icon icon="calendar-clock" color={colors.red[400]} />}
          isSelected={selectedComputedStatus === 'overdue'}
          onClick={() => onClickOverviewStatusCard('overdue')}
        />
      </GridItem>
      <GridItem>
        <InvoicesOverviewCard
          label="Paid"
          totalAmount={aggregates.paid.totalAmount}
          totalCount={aggregates.paid.count}
          icon={<Icon icon="circle-check" color={colors.green[400]} />}
          isSelected={selectedComputedStatus === 'paid'}
          onClick={() => onClickOverviewStatusCard('paid')}
        />
      </GridItem>
    </Grid>
  );
};

type InvoicesOverviewCardsProps = {
  aggregates: InvoiceAggregatesData | undefined;
  isLoading: boolean;
  selectedComputedStatus: HighlightComputedStatus | null;
  setSelectedComputedStatus: React.Dispatch<
    React.SetStateAction<HighlightComputedStatus | null>
  >;
};
export const InvoicesOverviewCards = ({
  aggregates,
  isLoading,
  selectedComputedStatus,
  setSelectedComputedStatus,
}: InvoicesOverviewCardsProps) => {
  if (isLoading || !aggregates) {
    return <InvoicesOverviewCardsSkeleton />;
  }

  return (
    <InvoicesOverviewCardsWithData
      aggregates={aggregates}
      selectedComputedStatus={selectedComputedStatus}
      setSelectedComputedStatus={setSelectedComputedStatus}
    />
  );
};
